import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { PermissionItem, Permissions } from '@eview/core/models/permissions';
import { selectPermissions } from '@eview/core/store/selectors/permissions.selector';
import { AppState } from '@eview/core/store/states/app.state';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators
} from '@angular/forms';
import { AuthHelpers } from '@eview/core/auth';
import { AllowedPriviledge } from '@eview/core/auth/auth';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UserService } from '@eview/core/users/user.service';
import { AuthCatalogFacade } from '@eview/core/store/facades/auth-catalog.facade';
import { selectPermissionsLoaded } from '@eview/core/store/selectors/auth-catalog.selectors';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'eview-permissions-management',
  templateUrl: 'permissions.component.html',
  styleUrls: ['permissions.component.scss']
})
export class PermissionsComponent implements OnInit, OnDestroy {
  @ViewChild('permissionModal') permissionModal: TemplateRef<unknown>;

  permissions$: Observable<Permissions>;
  listLoading = false;

  constructor(
    private store: Store<AppState>,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    private authCatalog: AuthCatalogFacade
  ) {
    this.subs = new Subscription();
    this.permissions$ = this.store.select(selectPermissions);
  }

  ngOnInit() {
    this.createpermissionForm();
    this.store
      .select(selectPermissionsLoaded)
      .pipe(take(1))
      .subscribe(loaded => {
        if (!loaded) {
          this.listLoading = true;
        }
      });
    this.authCatalog.ensurePermissions();
    this.subs.add(
      this.permissions$.subscribe(permissions => {
        if (permissions != null) {
          this.permissionsList = permissions.results ?? [];
          this.listLoading = false;
        }
      })
    );
  }

  private refreshPermissionsList(): void {
    this.listLoading = true;
    this.authCatalog.ensurePermissions(true);
  }

  createpermissionForm() {
    this.permissionForm = this.formBuilder.group({
      name: ['', [Validators.required, this.noSpecialCharsAtStart, Validators.maxLength(100)]],
      description: ['', [Validators.required, this.noSpecialCharsAtStart, Validators.maxLength(250)]],
    });
  }

  noSpecialCharsAtStart(control: any) {
    const value = control.value;
    if (!value) return null;
    const invalidChars = /^[.\-'"°,*()]$/;
    return invalidChars.test(value) ? { invalidStart: true } : null;
  }

  validarField(event: KeyboardEvent) {
    const char = event.key;
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const fieldName = target.getAttribute('formControlName');
    const maxLength = fieldName === 'name' ? 100 : 250;
    
    if (currentValue.length >= maxLength && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(char)) {
      event.preventDefault();
      return;
    }
    
    if (char === '´' && currentValue.trim() === '') {
      event.preventDefault();
      return;
    }
    
    const selectionStart = target.selectionStart || 0;
    if (selectionStart === 0 && /[\s.\-'"°,*()]/.test(char)) {
      event.preventDefault();
      return;
    }
    
    const regex = /^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-'"°,*()]$/;
    if (!regex.test(char)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent, fieldName: string) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const maxLength = fieldName === 'name' ? 100 : 250;
    let cleanedText = pastedText.replace(/^[\s.\-'"°,*()]+/, '').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-'"°,*()]/g, '').substring(0, maxLength);
    this.permissionForm.get(fieldName)?.setValue(cleanedText);
  }

  onCancelClick() {
    this.isEditing = false;
    this.editId = -1;
    this.permissionForm.reset();
    this.permissionForm.get('name')?.enable();
    this.modalService.dismissAll();
  }

  onEditClick(id: number) {
    this.isEditing = true;
    this.permissions$.pipe(
      map(data => data?.results ?? []),
      take(1)
    ).subscribe((item) => {
      const permission = item[id];
      if (permission) {
        this.editId = permission.id;
        this.permissionForm.patchValue({
          name: permission.name,
          description: permission.description
        });
        this.permissionForm.get('name')?.disable();
      }
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onDeleteClick(index: number) {
    this.selectedId = index;
  }

  openPermissionModal(index: number = -1) {
    if (!this.permissionModal) {
      return;
    }
    if (index !== -1) {
      this.permissions$.pipe(map(data => data?.results ?? []), take(1)).subscribe((items) => {
        const permission = items[index];
        if (!permission) {
          return;
        }
        this.isEditing = true;
        this.editId = permission.id;
        this.permissionForm.reset({
          name: permission.name,
          description: permission.description
        });
        this.permissionForm.get('name')?.disable();
        this.modalService.open(this.permissionModal, { centered: true, backdrop: 'static', size: 'lg' });
      });
      return;
    }
    this.isEditing = false;
    this.editId = -1;
    this.permissionForm.reset();
    this.permissionForm.get('name')?.enable();
    this.modalService.open(this.permissionModal, { centered: true, backdrop: 'static', size: 'lg' });
  }

  onConfirmClick(permissionId: number) {
    if (permissionId) {
      this.userService.deletePermission(permissionId).subscribe(() => {
        this.selectedId = -1;
        this.refreshPermissionsList();
      });
    }
  }

  onSubmitClick() {
    if (!this.permissionForm.valid) {
      return;
    }
    const reqBody = this.isEditing
      ? this.permissionForm.getRawValue()
      : this.permissionForm.value;
    if (!this.isEditing) {
      this.userService.addPermissions(reqBody).subscribe(() => {
        this.permissionForm.reset();
        this.refreshPermissionsList();
        this.modalService.dismissAll();
      });
    } else {
      this.userService.updatePermissions(reqBody, this.editId).subscribe(() => {
        this.isEditing = false;
        this.permissionForm.reset();
        this.permissionForm.get('name')?.enable();
        this.refreshPermissionsList();
        this.modalService.dismissAll();
      });
    }
  }

  subs: Subscription;

  AuthHelpers = AuthHelpers;

  permissionForm: UntypedFormGroup;

  AllowedPriviledge = AllowedPriviledge;

  selectedId: number = -1;

  editId: number = -1;

  isEditing: boolean = false;

  permissionsList: PermissionItem[] = [];
}
